import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import LazyImage from "../../../../SharedComponents/ShowMedia/LazyImage";
import { useTranslation } from "react-i18next";

const LayoutsContent = ({ itIsPlaying, item, startTime, pause_start_time, showAll = false }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const itemRefs = useRef({});
  const { paused } = useSelector((state) => state.device);

  const [elapsedSeconds, setElapsedSeconds] = useState(() => {
    const now = paused ? moment(pause_start_time) : moment();
    return now.diff(moment(startTime), "seconds");
  });

  useEffect(() => {
    let interval;
    if (!paused) {
      interval = setInterval(() => {
        const now = moment();
        setElapsedSeconds(now.diff(moment(startTime), "seconds"));
      }, 1000);
    } else if (pause_start_time) {
      const now = moment(pause_start_time);
      setElapsedSeconds(now.diff(moment(startTime), "seconds"));
    }
    return () => clearInterval(interval);
  }, [paused, startTime, pause_start_time]);

  const getCurrentPlayingIndex = (playlist, elapsedSeconds) => {
    let total = 0;
    for (let i = 0; i < playlist.length; i++) {
      total += Number(playlist[i].custom_duration || 0);
      if (elapsedSeconds < total) return i;
    }
    return -1;
  };

  useEffect(() => {
    if (!itIsPlaying) return;
    item.layout.forEach((layoutItem, layoutIndex) => {
      const playlist = layoutItem.campaign_media?.MAIN || [];
      const currentIndex = getCurrentPlayingIndex(playlist, elapsedSeconds);
      if (currentIndex !== -1) {
        const refKey = `${layoutIndex}-${currentIndex}`;
        const target = itemRefs.current[refKey];
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    });
  }, [elapsedSeconds, itIsPlaying, item.layout]);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-6 pt-2 overflow-y-auto ${showAll ? "" : "max-h-[300px]"} custom-scrollbar`}
    >
      {item.layout.map((layoutItem, layoutIndex) => {
        const playlist = layoutItem.campaign_media?.MAIN || [];
        const currentIndex = itIsPlaying ? getCurrentPlayingIndex(playlist, elapsedSeconds) : -1;

        return (
          <div key={layoutIndex} className="bg-slate-50/50 rounded-[1.5rem] p-3 border border-slate-100/50">
            {/* Split Screen Identifier */}
            <div className="flex justify-between items-center mb-4 px-2">
              <div className="flex gap-2 items-center">
                <div className="w-6 h-6 rounded bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-100">
                   <i className="fa-solid fa-shapes text-[10px] text-white"></i>
                </div>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-800">{layoutItem.screen_name || t("devices.preview_parallel_screen")}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
                {playlist.map((mediaItem, mediaIndex) => {
                  const refKey = `${layoutIndex}-${mediaIndex}`;
                  const isActive = mediaIndex === currentIndex;

                  return (
                    <div
                      key={refKey}
                      ref={el => itemRefs.current[refKey] = el}
                      className={`relative flex items-center gap-3 p-2.5 rounded-2xl border transition-all duration-300 group
                        ${isActive 
                            ? "bg-white border-[var(--pp-btn-honey)] shadow-xl shadow-[color-mix(in_srgb,var(--pp-btn-honey)_10%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--pp-btn-honey)_10%,transparent)]" 
                            : "bg-white/40 border-transparent hover:border-slate-200"}`}
                    >
                      {/* Active State Glimpse */}
                      {isActive && (
                         <div className="absolute left-0 top-3 bottom-3 w-[4px] bg-[var(--pp-btn-honey)] rounded-r-full"></div>
                      )}

                      <div className={`w-10 h-10 ml-1 shrink-0 rounded-xl flex items-center justify-center overflow-hidden border transition-all
                          ${isActive ? 'bg-white border-[color-mix(in_srgb,var(--pp-btn-honey)_25%,white)]' : 'bg-slate-100 border-slate-100 group-hover:bg-slate-200'}`}
                      >
                        {mediaItem.media_type ? (
                          ["HDMI", "CHANNEL", "RSS", "RSS_CUSTOM", "MULTI_3D"].includes(mediaItem.media_type) ? (
                            <span className="text-[8px] font-black text-slate-500 uppercase text-center leading-tight">{mediaItem.media_type.replace(/_/g, "\n")}</span>
                          ) : (
                            <LazyImage media={mediaItem} popupType="playlist_media_setting" index={mediaItem.id} styleClass="w-full h-full object-cover" />
                          )
                        ) : (
                           <LazyImage media={mediaItem} popupType="playlist_media_setting" index={mediaItem.id} styleClass="w-full h-full object-cover" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className={`text-[11px] font-bold truncate transition-colors ${isActive ? 'text-[var(--pp-btn-honey)]' : 'text-slate-700'}`}>
                          {mediaItem.media_name || mediaItem.playlist_name || mediaItem.layout_name || t("devices.preview_parallel_unit")}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                           <span className={`text-[9px] font-black tracking-tighter ${isActive ? 'text-[var(--pp-btn-honey)]' : 'text-slate-400'}`}>
                              {mediaItem.custom_duration}s {t("devices.preview_exposure")}
                           </span>
                           {isActive && (
                              <div className="flex items-center gap-1.5 ml-auto">
                                 <span className="text-[8px] font-black text-[var(--pp-btn-honey)] lowercase tracking-widest">{t("devices.preview_active")}</span>
                                 <div className="w-1.5 h-1.5 bg-[var(--pp-btn-honey)] rounded-full animate-pulse"></div>
                              </div>
                           )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LayoutsContent;
